/**
 * usePersonaWorkshop Hook
 *
 * Manages Persona Workshop session state, phase progression,
 * AI suggestions, and persona data accumulation.
 */

import { useState, useCallback } from 'react'
import { useAIStore } from '@/stores/aiStore'
import { personaWorkshopApi } from '@/lib/api/client'
import type {
  PersonaWorkshopSession,
  PersonaData,
  PersonaType,
  PersonaWorkshopMode,
  PersonaWorkshopPhaseId,
  PersonaSuggestion,
  PersonaExtractedEntity,
  EMPTY_PERSONA_DATA,
} from '@ispot/shared'

// ============================================================================
// TYPES
// ============================================================================

export interface PersonaWorkshopState {
  session: PersonaWorkshopSession | null
  sessions: PersonaWorkshopSession[]
  suggestions: PersonaSuggestion[]
  archetypes: string[]
  demographics: Record<string, string[]>
  quotes: string[]
  isLoading: boolean
  isSuggestionsLoading: boolean
  error: string | null
}

// ============================================================================
// HOOK
// ============================================================================

export function usePersonaWorkshop(accountId: string, projectId: string) {
  const [state, setState] = useState<PersonaWorkshopState>({
    session: null,
    sessions: [],
    suggestions: [],
    archetypes: [],
    demographics: {},
    quotes: [],
    isLoading: false,
    isSuggestionsLoading: false,
    error: null,
  })

  const { apiKey } = useAIStore()

  const loadSessions = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null }))
    try {
      const sessions = await personaWorkshopApi.listSessions(accountId, projectId)
      setState(s => ({ ...s, sessions: sessions as PersonaWorkshopSession[], isLoading: false }))
    } catch (error) {
      setState(s => ({ ...s, isLoading: false, error: error instanceof Error ? error.message : 'Failed to load sessions' }))
    }
  }, [accountId, projectId])

  const createSession = useCallback(async (personaType: PersonaType, mode: PersonaWorkshopMode) => {
    setState(s => ({ ...s, isLoading: true, error: null }))
    try {
      const session = await personaWorkshopApi.createSession(accountId, projectId, { personaType, mode })
      setState(s => ({ ...s, session: session as PersonaWorkshopSession, isLoading: false }))
      return session as PersonaWorkshopSession
    } catch (error) {
      setState(s => ({ ...s, isLoading: false, error: error instanceof Error ? error.message : 'Failed to create session' }))
      return null
    }
  }, [accountId, projectId])

  const loadSession = useCallback(async (sessionId: string) => {
    setState(s => ({ ...s, isLoading: true, error: null }))
    try {
      const session = await personaWorkshopApi.getSession(accountId, projectId, sessionId)
      setState(s => ({ ...s, session: session as PersonaWorkshopSession, isLoading: false }))
    } catch (error) {
      setState(s => ({ ...s, isLoading: false, error: error instanceof Error ? error.message : 'Failed to load session' }))
    }
  }, [accountId, projectId])

  const updateSession = useCallback(async (
    sessionId: string,
    updates: { currentPhase?: number; personaData?: Partial<PersonaData>; personaType?: PersonaType },
  ) => {
    try {
      const session = await personaWorkshopApi.updateSession(
        accountId, projectId, sessionId, updates,
      )
      setState(s => ({ ...s, session: session as PersonaWorkshopSession }))
      return session as PersonaWorkshopSession
    } catch (error) {
      setState(s => ({ ...s, error: error instanceof Error ? error.message : 'Failed to update session' }))
      return null
    }
  }, [accountId, projectId])

  const fetchSuggestions = useCallback(async (sessionId: string, phaseId: PersonaWorkshopPhaseId) => {
    if (!apiKey) {
      setState(s => ({ ...s, error: 'API key required for AI suggestions' }))
      return
    }

    setState(s => ({ ...s, isSuggestionsLoading: true, suggestions: [], archetypes: [], demographics: {}, quotes: [] }))
    try {
      const result = await personaWorkshopApi.suggest(
        accountId, projectId, sessionId, { phaseId, apiKey },
      ) as Record<string, unknown>

      setState(s => ({
        ...s,
        suggestions: (result.suggestions || []) as PersonaSuggestion[],
        archetypes: (result.archetypes || []) as string[],
        demographics: (result.demographics || {}) as Record<string, string[]>,
        quotes: (result.quotes || []) as string[],
        isSuggestionsLoading: false,
      }))
    } catch (error) {
      setState(s => ({ ...s, isSuggestionsLoading: false, error: error instanceof Error ? error.message : 'Failed to fetch suggestions' }))
    }
  }, [accountId, projectId, apiKey])

  const completeSession = useCallback(async (sessionId: string) => {
    setState(s => ({ ...s, isLoading: true }))
    try {
      const session = await personaWorkshopApi.complete(
        accountId, projectId, sessionId, { apiKey: apiKey || undefined },
      )
      setState(s => ({ ...s, session: session as PersonaWorkshopSession, isLoading: false }))
      return session as PersonaWorkshopSession
    } catch (error) {
      setState(s => ({ ...s, isLoading: false, error: error instanceof Error ? error.message : 'Failed to complete session' }))
      return null
    }
  }, [accountId, projectId, apiKey])

  return {
    ...state,
    loadSessions,
    createSession,
    loadSession,
    updateSession,
    fetchSuggestions,
    completeSession,
  }
}
